/*
 * A custom error type for errors coming from the task manager.
 * These errors will have errors stored in this.errors, which allows
 * us to batch errors from several handlers into one exception.
 */
class TaskManagerError extends Error {
  errors: Error[];

  constructor(message: string, errors: Error[]) {
    super(message);
    this.name = 'TaskManagerError';
    this.errors = errors;
  }

  getFirstErrorOfType = <ErrorType extends typeof Error>(
    errorType: ErrorType,
  ): Error | undefined => {
    let result;
    this.errors.some(error => {
      if (error instanceof TaskManagerError) {
        result = error.getFirstErrorOfType(errorType);
        return !!result;
      }
      if (error instanceof errorType.constructor || error instanceof errorType) {
        result = error;
        return true;
      }
      return false;
    });
    return result;
  };

  // Similar to Array.some this tests whether any Errors aggregated by TaskManager
  // match the provided Error type and message
  some = <ErrorType extends typeof Error>(errorType: ErrorType, msg: string): boolean => {
    return this.errors.some(error => error instanceof errorType && error.message === msg);
  };
}

export const isInstanceOf = <ErrorType extends typeof Error>(
  error: Error,
  errorType: ErrorType,
): boolean => {
  if (error instanceof TaskManagerError) {
    if (error.getFirstErrorOfType(errorType)) return true;
  }
  if (error instanceof errorType) return true;
  return false;
};

export const extractError = <ErrorType extends typeof Error>(
  error: Error,
  errorType: ErrorType,
): Error | undefined => {
  if (error instanceof TaskManagerError) {
    return error.getFirstErrorOfType(errorType);
  }
  return error;
};

export default TaskManagerError;
